import { useMemo } from 'react'
import { uniq } from 'lodash'
import moment from 'moment'
import {
  Availability,
  EhrAppointmentRecurrenceCadence,
  EhrAppointmentRecurrenceDay,
  PotentiallyRecurringSlots,
} from '@src/types'
import { dayOfWeekToRecurrenceDayLookup } from './constants'
import { buildTimeSlotOptionsFromSlots, toStartCaseOption } from './helpers'

const potentiallyRecurringSlotsCadenceLookup: Record<string, string> = {
  [EhrAppointmentRecurrenceCadence.Weekly]: 'weekly',
  [EhrAppointmentRecurrenceCadence.BiWeekly]: 'biWeekly',
  [EhrAppointmentRecurrenceCadence.Monthly]: 'monthly',
}

export const useDayTimeOptions = ({
  availabilities,
  cadence,
  days,
}: {
  availabilities: Availability[] | PotentiallyRecurringSlots | null
  cadence?: string
  days: EhrAppointmentRecurrenceDay[]
}) => {
  const dayOptions = useMemo(() => {
    if (!availabilities || !cadence) {
      return []
    }

    const potentiallyRecurringSlots = availabilities as PotentiallyRecurringSlots
    const cadenceKey = potentiallyRecurringSlotsCadenceLookup[
      cadence
    ] as keyof PotentiallyRecurringSlots
    const cadenceSlots = potentiallyRecurringSlots[cadenceKey] as string[]

    const availableDays = uniq(cadenceSlots.map((slot) => moment(slot).day()))
      .sort()
      .map((dayOfWeek) => dayOfWeekToRecurrenceDayLookup[dayOfWeek])
    return [EhrAppointmentRecurrenceDay.All, ...availableDays].map(toStartCaseOption)
  }, [availabilities, cadence])

  const timeSlotOptions = useMemo(() => {
    if (!availabilities || !cadence) {
      return []
    }

    const potentiallyRecurringSlots = availabilities as PotentiallyRecurringSlots
    const cadenceKey = potentiallyRecurringSlotsCadenceLookup[
      cadence
    ] as keyof PotentiallyRecurringSlots

    const cadenceSlots = potentiallyRecurringSlots[cadenceKey] as string[]
    return buildTimeSlotOptionsFromSlots(cadenceSlots, days)
  }, [availabilities, days, cadence])

  return { dayOptions, timeSlotOptions }
}
